import React, { createContext, FC, useState, useRef, useEffect, useContext, useMemo, useCallback } from "react";
import { FeatureCollection, Geometry } from "geojson";
import { MarketExpansionFiltersContext } from "@app/overviews/market-expansion-dashboard/contexts/MarketExpansionFiltersContext";
import { cloneDeep } from "lodash";
import MarketExpansionService from "@app/overviews/market-expansion-dashboard/marketExpansionService";
import { convertToFeatureCollection } from "@app/overviews/market-expansion-dashboard/shared/marketExpansionMapHelper";
import { ProviderLandscapeCountyCaseCount } from "@app/overviews/market-expansion-dashboard/shared/ProviderLandscapeCountyCaseCounts";
import { ProviderLandscapeCounty } from "@app/overviews/market-expansion-dashboard/shared/ProviderLandscapeCounty";
import { ProviderLandscapeContractEntityCaseCount } from "@app/overviews/market-expansion-dashboard/shared/ProviderLandscapeContractEntityCaseCount";
import { CONTRACT_ENTITY_COLOR_SLOT_KEY_PREFIX, CONTRACT_ENTITY_REST_COLOR_SLOT_KEY } from "@app/overviews/market-expansion-dashboard/shared/ContractEntityColorThemeConstants";

interface ProviderLandscapeMapDataContextProps {
  contractEntityServiceAreaCountyLevelSummaries: ProviderLandscapeContractEntityCaseCount[];
  countySummaryData: ProviderLandscapeCounty[] | undefined;
  getContractEntityColorSlotKey: (groupingId: number) => string;
  providerLandscapeShadedCountyData: FeatureCollection<Geometry, ProviderLandscapeCounty> | undefined;
  serviceAreaCasesByCCN: Map<string, number>;
  serviceAreaCasesByGroupingId: Map<number, number>;
}

const initialValues: ProviderLandscapeMapDataContextProps = {
  contractEntityServiceAreaCountyLevelSummaries: [],
  countySummaryData: undefined,
  getContractEntityColorSlotKey: () => CONTRACT_ENTITY_REST_COLOR_SLOT_KEY,
  providerLandscapeShadedCountyData: undefined,
  serviceAreaCasesByCCN: new Map(),
  serviceAreaCasesByGroupingId: new Map(),
}

const ProviderLandscapeMapDataContext = createContext(initialValues);

const ProviderLandscapeMapDataContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {

  const filtersContext = useContext(MarketExpansionFiltersContext);
  const marketExpansionService = useRef(new MarketExpansionService()).current;

  const [providerLandscapeShadedCountyData, setProviderLandscapeShadedCountyData] = useState<FeatureCollection<Geometry, ProviderLandscapeCounty> | undefined>(initialValues.providerLandscapeShadedCountyData);
  const [countySummaryData, setCountySummaryData] = useState<ProviderLandscapeCounty[] | undefined>(initialValues.countySummaryData);

  useEffect(() => {
    // isCurrent guard for now to protect against stale data.
    // TODO: update this service call to use tanstack User Story: 208127
    let isCurrent = true;

    if (filtersContext.countyOptions.length > 0) {
      buildProviderLandscapeShadedCountyMapFeatures(() => isCurrent);
    } else {
      setProviderLandscapeShadedCountyData(convertToFeatureCollection([]));
      setCountySummaryData([]);
    }

    return () => {
      isCurrent = false;
    };
  }, [filtersContext.countyOptions, filtersContext.selectedLineOfBusiness]);

  const buildProviderLandscapeShadedCountyMapFeatures = (isCurrent: () => boolean) => {
    setProviderLandscapeShadedCountyData(undefined);
    setCountySummaryData(undefined);

    let counties = cloneDeep(filtersContext.countyOptions as ProviderLandscapeCounty[]);

    marketExpansionService.searchProviderLandscapeRecords({
      countySSAs: filtersContext.countyOptions.map(c => c.countySSA),
      lineOfBusiness: filtersContext.selectedLineOfBusiness
    }).then((data: ProviderLandscapeCountyCaseCount[]) => {
      if (!isCurrent()) return;

      if (!!data) {
        const casesByCountySSA = new Map(
          data.map((e) => [e.countySSA, e.totalCaseCount])
        );

        const contractEntitiesBySSA = new Map(
          data.map((e) => [e.countySSA, e.contractEntities])
        );

        counties = counties.map((county) => ({
          ...county,
          contractEntities: contractEntitiesBySSA.get(county.countySSA) ?? [],
          totalCaseCount: casesByCountySSA.get(county.countySSA) ?? 0,
        }));

        setCountySummaryData(counties);
      } else {
        setCountySummaryData([]);
      }

      const countyFeatures = convertToFeatureCollection(counties);

      if (countyFeatures.features.length) {
        setProviderLandscapeShadedCountyData(countyFeatures);
      }
    })
    .catch(() => {
      if (!isCurrent()) return;

      setCountySummaryData([]);
      setProviderLandscapeShadedCountyData(convertToFeatureCollection([]));
    });
  }

  const contractEntityServiceAreaCountyLevelSummaries = useMemo(() => {
    const serviceAreaCountySummaries = (countySummaryData ?? []).filter((county) =>
      filtersContext.selectedCounties.includes(county.countySSA),
    );
    return serviceAreaCountySummaries.flatMap((county) => county.contractEntities);
  }, [filtersContext.selectedCounties, countySummaryData]);

  const serviceAreaCasesByGroupingId = useMemo(() => {
    const contractEntityCases = new Map<number, number>();
    contractEntityServiceAreaCountyLevelSummaries.forEach((ceSummary) => {
      const groupingId = ceSummary.groupingId;
      const runningTotal = contractEntityCases.get(groupingId) ?? 0;
      contractEntityCases.set(groupingId, runningTotal + ceSummary.caseCount);
    });
    return contractEntityCases;
  }, [contractEntityServiceAreaCountyLevelSummaries]);

  const serviceAreaCasesByCCN = useMemo(() => {
    const providerCases = new Map<string, number>();
    contractEntityServiceAreaCountyLevelSummaries.flatMap((ceSummary) => ceSummary.providers).forEach((p) => {
      const existingCount = providerCases.get(p.ccn) ?? 0;
      providerCases.set(p.ccn, existingCount + p.caseCount);
    });
    return providerCases;
  }, [contractEntityServiceAreaCountyLevelSummaries]);

  /** Contains ContractEntityColorTheme color slot keys for groupingIds with the highest total service area cases. */
  const contractEntityColorSlotKeyMap = useMemo(() => {
    const groupingIdCaseCountPairs = [...serviceAreaCasesByGroupingId.entries()];
    const groupingIdsSortedByCases = groupingIdCaseCountPairs.sort((a, b) => b[1] - a[1]).map((pair) => pair[0]);

    const colorMap = new Map<number, string>();
    groupingIdsSortedByCases.slice(0, 5).forEach((groupingId, i) => {
      colorMap.set(groupingId, `${CONTRACT_ENTITY_COLOR_SLOT_KEY_PREFIX}${i + 1}`);
    });
    return colorMap;
  }, [serviceAreaCasesByGroupingId]);

  const getContractEntityColorSlotKey = useCallback((groupingId: number): string => {
    return contractEntityColorSlotKeyMap.get(groupingId) ?? CONTRACT_ENTITY_REST_COLOR_SLOT_KEY;
  }, [contractEntityColorSlotKeyMap]);

  const contextValue: ProviderLandscapeMapDataContextProps = {
    contractEntityServiceAreaCountyLevelSummaries,
    countySummaryData,
    getContractEntityColorSlotKey,
    providerLandscapeShadedCountyData,
    serviceAreaCasesByCCN,
    serviceAreaCasesByGroupingId,
  };

  return (
    <ProviderLandscapeMapDataContext.Provider value={contextValue}>
      {props.children}
    </ProviderLandscapeMapDataContext.Provider>
  );
}

export { ProviderLandscapeMapDataContext, ProviderLandscapeMapDataContextProvider, ProviderLandscapeMapDataContextProps };
